import { useState } from 'react';

const SUBMIT_ADDR = 'https://readdy.ai/api/form/da2lb7iamq5jpe1hrvo0';

export default function Newsletter() {
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);
    const honeypot = ((fd.get('website_alt') as string) || '').trim();

    // 蜜罐命中：不提交，但展示与正常一致的成功反馈，避免给机器人明确信号
    if (honeypot) {
      setStatus('success');
      form.reset();
      return;
    }

    const payload = new URLSearchParams();
    fd.forEach((value, key) => {
      if (key === 'website_alt') return;
      payload.append(key, value as string);
    });

    setStatus('loading');
    setFormError('');

    try {
      const res = await fetch(SUBMIT_ADDR, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });
      const responseText = await res.text();
      let parsed: Record<string, unknown> | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const meta = (parsed?.meta as Record<string, unknown> | undefined) || {};
      const serverMsg =
        (meta.message as string) ||
        (parsed?.message as string) ||
        (meta.detail as string) ||
        responseText ||
        '';

      if (!res.ok || !parsed || parsed.code !== 'OK' || /spam/i.test(String(serverMsg))) {
        setStatus('error');
        setFormError(String(serverMsg).trim() ? String(serverMsg) : '提交失败，请稍后再试');
        return;
      }

      setStatus('success');
      form.reset();
    } catch {
      setStatus('error');
      setFormError('网络异常，请稍后再试');
    }
  }

  return (
    <section id="subscribe" className="py-14 md:py-20 bg-background-100">
      <div className="mx-auto max-w-6xl px-4 md:px-8">
        <div className="relative overflow-hidden rounded-3xl bg-primary-600 px-6 py-12 md:px-12 md:py-16">
          <div className="absolute -right-10 -top-10 w-56 h-56 rounded-full bg-primary-400/40 blur-3xl" />
          <div className="absolute -left-12 -bottom-16 w-64 h-64 rounded-full bg-accent-400/30 blur-3xl" />
          <div className="relative grid items-center gap-8 lg:grid-cols-2">
            <div>
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-background-50/15 text-background-50 text-xs font-semibold">
                <i className="ri-mail-send-line w-4 h-4 flex items-center justify-center" />
                订阅周刊
              </span>
              <h2 className="mt-4 font-heading text-2xl md:text-4xl text-background-50 leading-tight">
                每周精选韩漫
                <br />
                第一时间送达
              </h2>
              <p className="mt-3 text-sm md:text-base text-background-100 leading-relaxed">
                订阅《漫阅周刊》，每周为你整理人气新作、完结佳作与编辑私藏推荐，不打扰、不刷屏，喜欢韩漫的你值得拥有。
              </p>
            </div>

            <div className="rounded-2xl bg-background-50 p-5 md:p-7">
              {status === 'success' ? (
                <div className="flex flex-col items-center py-8 text-center">
                  <span className="w-14 h-14 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                    <i className="ri-check-line text-2xl w-6 h-6 flex items-center justify-center" />
                  </span>
                  <p className="mt-4 font-heading text-lg text-foreground-950">订阅成功！</p>
                  <p className="mt-1 text-sm text-foreground-500">感谢订阅，每周精选韩漫即将送达你的邮箱。</p>
                  <button
                    type="button"
                    onClick={() => setStatus('idle')}
                    className="mt-5 text-sm font-semibold text-primary-600 hover:text-primary-700 cursor-pointer"
                  >
                    再订阅一个邮箱
                  </button>
                </div>
              ) : (
                <form className="flex flex-col gap-3" onSubmit={handleSubmit} data-readdy-form="韩漫周刊订阅表单" aria-label="订阅韩漫周刊">
                  <input
                    type="text"
                    name="nickname"
                    placeholder="怎么称呼你（可选）"
                    className="w-full px-4 py-3 rounded-lg bg-background-100 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400"
                  />
                  <input
                    type="email"
                    name="email"
                    required
                    placeholder="请输入你的邮箱"
                    className="w-full px-4 py-3 rounded-lg bg-background-100 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400"
                  />
                  <input
                    type="text"
                    name="website_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="field-web"
                  />
                  <button
                    type="submit"
                    disabled={status === 'loading'}
                    className="mt-1 w-full py-3 rounded-lg bg-primary-500 text-background-50 text-sm font-bold hover:bg-primary-600 disabled:opacity-60 whitespace-nowrap cursor-pointer"
                  >
                    {status === 'loading' ? '提交中…' : '立即订阅'}
                  </button>
                  {status === 'error' && (
                    <p className="text-sm text-primary-700 bg-primary-100 px-3 py-2 rounded-lg" role="alert">
                      {formError}
                    </p>
                  )}
                  <p className="text-xs text-foreground-500 text-center">订阅即表示同意接收我们的精选内容，可随时退订。</p>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}